import { Injectable, SecurityContext } from '@angular/core';
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
} from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class SanitizeService {
  constructor(private readonly _sanitizer: DomSanitizer) {}

  /**
   * Same signature as before. Works for BOTH:
   * - normal HTML (sanitized by Angular HTML sanitizer)
   * - SVG markup (strict SVG whitelist sanitizer, then trusted)
   *
   * NOTE:
   * We still must return SafeHtml -> must use bypassSecurityTrustHtml,
   * but ONLY after we sanitize/whitelist.
   */
  public sanitizeContent(content: string, preserveStyles = false): SafeHtml {
    if (!content) {
      return this._sanitizer.bypassSecurityTrustHtml('');
    }

    const trimmed = content.trim();

    // If it looks like SVG, Angular HTML sanitization often strips it.
    // So we do strict SVG sanitization + then trust the result.
    if (this._looksLikeSvg(trimmed)) {
      const safeSvg = this._sanitizeSvgStrict(trimmed, {
        preserveStyles: false, // styles inside SVG are risky; keep false
      });
      return this._sanitizer.bypassSecurityTrustHtml(safeSvg);
    }

    // For HTML: DO NOT re-inject <style> blocks unless you're 100% sure content is trusted.
    // Your old code re-injected style tags (risky). Here we keep behavior behind preserveStyles,
    // but we sanitize style blocks aggressively.
    if (preserveStyles) {
      // If we must preserve styles (like in emails), we trust the HTML.
      // We still do basic regex cleaning for known dangerous patterns.
      const safeHtml = this._sanitizeHtmlPreserveStylesRegexOnly(trimmed);
      return this._sanitizer.bypassSecurityTrustHtml(safeHtml);
    }

    const sanitized =
      this._sanitizer.sanitize(SecurityContext.HTML, trimmed) || '';
    // Return SafeHtml as before (no consumer changes),
    // but it's sanitized first, then trusted to prevent Angular stripping it further.
    return this._sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  /**
   * Same signature as before.
   * Must return SafeResourceUrl (so bypass is required),
   * but we validate first to avoid javascript:/data: etc.
   */
  public sanitizeIframeUrl(url: string): SafeResourceUrl {
    const safe = this._sanitizeUrlStrict(url);
    return this._sanitizer.bypassSecurityTrustResourceUrl(safe);
  }

  public sanitizePhoneNumber(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  // -------------------- Helpers --------------------

  private _looksLikeSvg(input: string): boolean {
    // tolerate XML header, whitespace, and uppercase tags
    return (
      /^<\?xml[\s\S]*?\?>\s*<svg[\s>]/i.test(input) || /^<svg[\s>]/i.test(input)
    );
  }

  /**
   * Strict SVG sanitizer:
   * - allows only a small set of tags/attributes used by icons
   * - removes scripts, foreignObject, event handlers, style tags, hrefs
   * - returns a string that is safe to trust
   */
  private _sanitizeSvgStrict(
    svgString: string,
    opts: { preserveStyles: boolean }
  ): string {
    try {
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      const root = doc.documentElement;

      if (root.tagName.toLowerCase() !== 'svg') {
        return '';
      }

      // Remove dangerous elements outright
      const forbidden = [
        'script',
        'foreignObject',
        'iframe',
        'object',
        'embed',
      ];
      forbidden.forEach((tag) => {
        doc.querySelectorAll(tag).forEach((n) => n.remove());
      });

      // For icons, you usually only need these tags.
      const allowedTags = new Set([
        'svg',
        'path',
        'g',
        'circle',
        'rect',
        'line',
        'polyline',
        'polygon',
        'defs',
        'lineargradient',
        'radialgradient',
        'stop',
        'clippath',
        'mask',
        'symbol',
        'image',
        'use',
      ]);

      // Keep this tight.
      const allowedAttrs = new Set([
        'viewbox',
        'xmlns',
        'fill',
        'stroke',
        'stroke-width',
        'stroke-linecap',
        'stroke-linejoin',
        'fill-rule',
        'clip-rule',
        'opacity',
        'd',
        'cx',
        'cy',
        'r',
        'x',
        'y',
        'x1',
        'y1',
        'x2',
        'y2',
        'rx',
        'ry',
        'points',
        'transform',
        'height',
        'width',
        'id',
        'fill-opacity',
        'stroke-opacity',
        'stop-opacity',
        'stop-color',
        'gradientunits',
        'gradienttransform',
        'offset',
        'clip-path',
        'mask',
        'style',
      ]);

      const cleanNode = (node: Element): Element | null => {
        const tag = node.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          return null;
        }

        const el = document.createElementNS(
          'http://www.w3.org/2000/svg',
          node.tagName
        );

        for (const attr of Array.from(node.attributes)) {
          const name = attr.name;
          const lname = name.toLowerCase();
          const value = attr.value;

          // Block event handlers and inline styles
          if (lname.startsWith('on')) {
            continue;
          }
          if (!opts.preserveStyles && lname === 'style') {
            continue;
          }

          // Block href/xlink:href that could contain javascript:/data:
          if (lname === 'href' || lname === 'xlink:href') {
            if (/^\s*(javascript:|data:)/i.test(value)) {
              continue;
            }
          }

          if (
            !allowedAttrs.has(name) &&
            !allowedAttrs.has(lname) &&
            lname !== 'href' &&
            lname !== 'xlink:href'
          ) {
            continue;
          }
          el.setAttribute(name, value);
        }

        for (const child of Array.from(node.children)) {
          const cleaned = cleanNode(child);
          if (cleaned) {
            el.appendChild(cleaned);
          }
        }

        return el;
      };

      const cleanedRoot = cleanNode(root) as SVGSVGElement | null;
      if (!cleanedRoot) {
        return '';
      }

      // Ensure xmlns exists
      if (!cleanedRoot.getAttribute('xmlns')) {
        cleanedRoot.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      }

      return new XMLSerializer().serializeToString(cleanedRoot);
    } catch {
      return '';
    }
  }

  /**
   * If preserveStyles=true, we walk the DOM and only remove dangerous elements/attributes.
   * This preserves inline style="..." and <style> blocks safely.
   */
  private _sanitizeHtmlPreserveStylesRegexOnly(html: string): string {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const forbiddenTags = new Set([
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'base',
        'applet',
        'meta',
        'link',
      ]);

      const cleanNode = (node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          const tag = el.tagName.toLowerCase();

          if (forbiddenTags.has(tag)) {
            el.remove();
            return;
          }

          // Remove dangerous attributes
          const attrs = Array.from(el.attributes);
          for (const attr of attrs) {
            const name = attr.name.toLowerCase();
            const val = attr.value.toLowerCase();

            // Block event handlers
            if (name.startsWith('on')) {
              el.removeAttribute(attr.name);
              continue;
            }

            // Block dangerous links
            if (
              (name === 'href' || name === 'src' || name === 'action') &&
              (val.includes('javascript:') || val.includes('data:'))
            ) {
              el.removeAttribute(attr.name);
              continue;
            }

            // Block dangerous CSS patterns in style attribute
            if (name === 'style' && this._isCssDangerous(val)) {
              el.removeAttribute(attr.name);
              continue;
            }
          }

          // Special handling for <style> tags content
          if (tag === 'style') {
            if (this._isCssDangerous(el.textContent || '')) {
              el.remove();
              return;
            }
          }
        }

        // Recurse children
        node.childNodes.forEach((child) => cleanNode(child));
      };

      doc.body.childNodes.forEach((node) => cleanNode(node));
      return doc.body.innerHTML;
    } catch (e) {
      console.error('PreserveStyle sanitization failed:', e);
      return this._sanitizer.sanitize(SecurityContext.HTML, html) || '';
    }
  }

  private _isCssDangerous(css: string): boolean {
    return /@import|expression\s*\(|url\s*\(\s*['"]?\s*(javascript:|data:)/i.test(
      css
    );
  }

  private _sanitizeUrlStrict(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return '';
    }

    // Allow same-origin relative URLs
    if (trimmed.startsWith('/')) {
      return trimmed;
    }

    // Allow only https? schemes
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    // Block javascript:, data:, blob:, file:, etc.
    return '';
  }
}
